import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  DatePicker,
  Typography,
  App,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import "dayjs/locale/es";
import { getCompetitionRankingVideos, listCompetitions } from '../../services/competitions';
import { useNavigate } from 'react-router-dom';

dayjs.locale("es");


const { Title } = Typography;
const { useApp } = App;

const EvaluationHistoryList = () => {
  const { message } = useApp();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // filtros draft
  const [draftStartDate, setDraftStartDate] = useState(null);
  const [draftEndDate, setDraftEndDate] = useState(null);
  
  const [loadingRowKey, setLoadingRowKey] = useState(null);



  const fetchData = useCallback(
    async (
      page,
      pageSize,
      srt,
      sd,
      ed,
    ) => {
      setLoading(true);
      try {
        const params = {
          page: page || pagination.current,
          pageSize: pageSize || pagination.pageSize,
          sortField: srt?.field,
          sortOrder: srt?.order,
          ...(sd && { startDate: sd }),
          ...(ed && { endDate: ed }),
          statusDescription: 'Terminada',
        };

        const { items, total } = await listCompetitions(params);

        setData(items || []);
        setPagination(prev => ({
          ...prev,
          current: page || prev.current,
          pageSize: pageSize || prev.pageSize,
          total: total || 0,
        }));
      } catch {
        message.error('No se pudieron cargar el historial');
      } finally {
        setLoading(false);
      }
    },
    [pagination, message]
  );

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = ({
    startDate = null,
    endDate = null,
  } = {}) => {
    setStartDate(startDate);
    setEndDate(endDate);
    
    fetchData(
      1,
      pagination.pageSize,
      sorter,
      startDate,
      endDate,
    );
  };

  const handleSearch = () => {
    applyFilters({
      startDate: draftStartDate,
      endDate: draftEndDate,
    });
  };

  const handleClearFilters = () => {
    setDraftStartDate(null);
    setDraftEndDate(null);
    applyFilters();
  };

  const onTableChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(
      pag.current,
      pag.pageSize,
      srt,
      startDate,
      endDate,
    );
  };

  const toggleExpand = async (record) => {
    const key = record.id;
    // Si ya está expandido → colapsar
    if (expandedRowKeys.includes(key)) {
      setExpandedRowKeys([]);
      return;
    }

    try {
      setLoadingRowKey(key);
      
      // Solo consulta si aún no tiene videos cargados
      if (!record.winners) {
        const items = await getCompetitionRankingVideos(key, record.finalistLimit);
        const winners = items.items ?? undefined;
        setData(prev =>
          prev.map(item =>
            item.id === key
              ? { ...item, winners }
              : item
          )
        );
      }

      setExpandedRowKeys([key]);
    } catch {
      message.error('No se pudieron cargar los videos');
    } finally {
      setLoadingRowKey(null);
    }
  };


  const expandedRowRender = (record) => {
    if (loadingRowKey === record.id) {
      return <div style={{ textAlign: 'center' }}>Cargando...</div>;
    }

    if (!record.winners?.length) {
      return <div style={{ textAlign: 'center' }}>Sin videos</div>;
    }

    const sortedData = [...record.winners].sort(
      (a, b) => a.position - b.position || b.globalScore - a.globalScore
    );

    const judgesMap = new Map();

    sortedData.forEach(video => {
      video.evaluations?.forEach(ev => {
        judgesMap.set(ev.judge.id, ev.judge);
      });
    });

    const judges = Array.from(judgesMap.values());

    const judgeColumns = judges.map(judge => ({
      title: `Juez ${judge.firstName} ${judge.lastName}`,
      align: 'center',
      render: (_, row) => {
        const evaluation = row.evaluations?.find(
          ev => ev.judge.id === judge.id
        );
        return Number(evaluation?.judgeScore).toFixed(3) ?? '-';
      }
    }));

    return (
      <Table
        className='winners-table'
        rowKey="id"
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={sortedData}
        columns={[
          {
            title: 'Participante',
            render: (_, r) =>
              (`${r.participant?.firstName ?? ''} ${r.participant?.lastName ?? ''} ${r.participant?.secondLastName ?? ''}`.trim()) || r.playerName
          },
          ...judgeColumns,
          {
            title: 'Promedio jueces',
            dataIndex: 'totalScore',
            align: 'center',
            render: (_, r) =>
              (Number(_ || 0)).toFixed(3)
          },
          {
            title: 'IA',
            dataIndex: 'aiTotalScore',
            align: 'center',
            render: (_, r) =>
              (Number(_ || 0) * 10).toFixed(3)
          },
          {
            title: 'Total',
            dataIndex: 'juryScore',
            align: 'center',
            render: (_, r) =>
              (Number(_ || 0)).toFixed(3)
          },
          {
            title: 'Puntaje votaciones',
            dataIndex: 'publicScore',
            align: 'center',
            render: (_, r) =>
              (Number(_ || 0)).toFixed(3)
          },
          {
            title: 'Evaluación Final',
            dataIndex: 'globalScore',
            align: 'center',
            render: (_, r) =>
              (Number(_ || 0)).toFixed(3)
          },
        ]}
      />
    );
  };


  const columns = [
    {
      title: 'Competencia',
      dataIndex: 'description',
    },
    {
      title: 'Estatus',
      dataIndex: 'statusId',

      render: (_, r) => r.status?.description || '-'
    },
    {
      title: 'Fecha Inicio',
      dataIndex: 'startDate',
      render: (_, r) => _? dayjs(_).format('DD/MM/YYYY') : '-',
    },

    {
      title: 'Fecha Fin',
      dataIndex: 'endDate',
      render: (_, r) => _? dayjs(_).format('DD/MM/YYYY') : '-',
    },

    {
      title: 'Videos finalistas',
      dataIndex: 'finalistLimit',
      align: 'center',
    },
    {
      title: 'Evaluación Ganadora',
      dataIndex: 'videos',
      align: 'center',
      render: (_, r) => r?.videos[0]?.globalScore || '-',
    },
    {
      title: 'Acciones',
      render: (_, r) => (<>
        <Button
          type="link"
          icon={expandedRowKeys.includes(r.id) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={() => toggleExpand(r)}
        >
          {expandedRowKeys.includes(r.id) ? 'Ocultar' : 'Ver '}
        </Button>
        <Button
          type="link"
          onClick={() => navigate(`/evaluations/history/${r.id}/evaluations`)}
        >
          Detalles
        </Button>
      </>),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>Historial de Evaluaciones</Title>
      </div>
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <DatePicker
            placeholder="Inicio"
            value={draftStartDate ? dayjs(draftStartDate, 'YYYY-MM-DD') : null}
            onChange={(_, d) => setDraftStartDate(d)}
          />
          <DatePicker
            placeholder="Fin"
            value={draftEndDate ? dayjs(draftEndDate, 'YYYY-MM-DD') : null}
            onChange={(_, d) => setDraftEndDate(d)}
          />
          <Tooltip title="Buscar">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            />
          </Tooltip>
          <Tooltip title="Limpiar filtros">
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            />
          </Tooltip>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowKeys,
            expandedRowRender,
            // rowExpandable: r => r.videos?.length > 0,
            expandIcon: () => null,
            showExpandColumn: false,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            showTotal: t => `Total: ${t} competencias`,
          }}
          onChange={onTableChange}
        />
      </Card>
    </div>
  );
};

export default EvaluationHistoryList;
